import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from '#src/components/landing/WalletLanding/index.tsx';
import { winAssets } from '#src/pages/assets/assets.ts';

import heroBg from './assets/hero/bg.webp';
import heroPlayersLeftSide from './assets/hero/players/leftSidePlayers.webp';
import heroPlayersRightSide from './assets/hero/players/rightSidePlayers.webp';
import leagueLogo from './assets/league-logo.webp';
import playField from './assets/play/field.webp';
import lineup from './assets/play/lineup.webp';
import scoutBg from './assets/scout/background.webp';
import players from './assets/scout/players.webp';
import social from './assets/social.webp';

const messages = defineMessages({
  metadatasTitle: {
    defaultMessage: 'The Real Fantasy Bundesliga Game – Sorare Football',
  },
  metadatasDescription: {
    defaultMessage:
      'Bundesliga: Play Sorare’s free fantasy Bundesliga game. Collect, buy, sell, and compete with ownable digital player cards to win rewards.',
  },
});

const heroAssets = {
  background: heroBg,
  players: [
    {
      pictureUrl: heroPlayersLeftSide,
      height: 575,
      width: 564,
    },
    {
      pictureUrl: heroPlayersRightSide,
      height: 563,
      width: 484,
    },
  ],
};

const scoutAssets = {
  background: scoutBg,
  players,
};

const playAssets = {
  background: playField,
  lineup,
};

const FootballPage = () => {
  const { generateSportContextPath } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: social,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (isLoggedIn) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} replace />;
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_GERMANY_LIMITED,
        So5LeaderboardType.IN_SEASON_GERMANY_UNIQUE,
        So5LeaderboardType.IN_SEASON_GERMANY_RARE,
      ]}
      leagueLogo={
        <Img src={leagueLogo} alt="Bundesliga" width="240" height="80" />
      }
      league={League.Bundesliga}
    />
  );
};

export default FootballPage;
